import {
  SensorCalibStatus,
  EncoderDirection,
  HallsensorDirection,
  HomingCalibStatus,
  IEncoderCalResultResponse,
  IHallSensorCalResultResponse,
  IHandshakeResponse,
  IHomingCalResultResponse,
  ISetPositionSensorLimitsResponse,
  IStartParamDetectRequest,
  IStartParamDetectResponse,
  IZeroDareSensorResponse,
  IGetSensorCalStatusResponse,
  PacketId,
  deserialize,
  inputReportID,
  serializeSetPositionSensorLimitsRequest,
  serializeStartEncoderCalRequest,
  serializeStartHallSensorCalRequest,
  serializeStartHomingCalRequest,
  serializeStartParamDetectRequest,
  serializeZeroDareSensorRequest,
  serializeGetSensorCalStatusRequest
} from '../utils/common-types'
import { HIDRequestMultiTimeout, hidRequest, hidRequestMulti, hidSend } from './hid'
import {
  FlashAction,
  FlashManagementCommand,
  IFlashMgmtRequest,
  PermanentFailure,
  ProgressCb,
  fileToBytes,
  getCStoreRegionOffset,
  isFlashOp,
  serializeFlashMgmtRequest,
  getCrcData,
  calcCrcMpeg2,
  calcCrcStm32Flash
} from './flashing/common'
import {
  enterDFUMode,
  FirmwareUpdateDFU,
  supportsDFU,
  productNameSupportsDFU
} from './flashing/dfu'
import { CStore, TagsToml, CodesToml, Op, OpType } from 'cstore/cstore'
import { tags, codes } from 'cstore/toml/toml'

const PROGRAM_CHUNK_WORD_SIZE = 12 // 12 x uint32

const PARAM_DETECT_INITIAL_TIMEOUT_MS = 20000 // 20 seconds from user initiation to first packet
const PARAM_DETECT_V1_TIMEOUT_MS = 90000 // 1 minute 30 seconds from first packet to second, but can be lengthened
const ENCODER_CALIBRATION_TIMEOUT_MS = 120000 // 2 minutes
const HALLSENSOR_CALIBRATION_TIMEOUT_MS = 120000 // 2 minutes
const HOMING_CALIBRATION_TIMEOUT_MS = 60000 // 1 minute
const ZERO_DARE_SENSOR_TIMEOUT_MS = 5000 // 5 seconds
const FLASH_MGMT_DEFAULT_TIMEOUT_MS = 10000 // 10s -- I heard the STM32H7 takes 2-4s to erase
const ERASE_BANK_TIMEOUT_MS = 10000 // 10s -- I heard the STM32H7 takes 2-4s to erase

// Only sub-commands 2, 4, and 7 include a memory offset at byte 8
const OFFSET_BEARING_COMMANDS = new Set([
  FlashManagementCommand.PROGRAM_MEMORY,
  FlashManagementCommand.READ_FLASH,
  FlashManagementCommand.WRITE_FLASH_CONFIG
])

const u32String = (u32: number) => {
  return u32.toString(16).padStart(8, '0')
}

export const deviceId = (handshakeResponse: IHandshakeResponse) => {
  return `${u32String(handshakeResponse.deviceId1)}${u32String(handshakeResponse.deviceId2)}${u32String(handshakeResponse.deviceId3)}`.toUpperCase()
}

// Enqueue operations to ensure they are executed sequentially per-device
// This is necessary to avoid issues with concurrent operations on the same device
// Each operation is added to the chain, and the chain is stored in a WeakMap. When an operation is added, it is chained onto the previous operation for that device
// Once an operation completes, it checks if it is the last operation in the chain for that device, and if so, it removes the entry from the WeakMap.
let deviceQueue: WeakMap<HIDDevice, Promise<unknown>> = new WeakMap()

export const _resetDeviceQueueForTesting = () => {
  deviceQueue = new WeakMap()
}
export const _getDeviceQueueForTesting = () => deviceQueue

export function enqueueDevice<T>(device: HIDDevice, fn: () => Promise<T>, retries = 3): Promise<T> {
  // Chain onto the previous operation
  const prev = deviceQueue.get(device) ?? Promise.resolve()
  const result = prev.then(() => retry(fn, retries))

  // Store the tail, only delete the queue entry if nothing new was queued after
  const tail = result
    .then(() => {
      if (deviceQueue.get(device) === tail) deviceQueue.delete(device)
    })
    .catch((e) => {
      if (deviceQueue.get(device) === tail) deviceQueue.delete(device)
      console.error(`Flash operation failed after ${retries} retries.`)
      throw e
    })
  tail.catch((e) => {
    console.error(`Flash operation failed after ${retries} retries.`, e)
  })
  deviceQueue.set(device, tail)
  return result
}

export async function retry<T>(fn: () => Promise<T>, attempts: number): Promise<T> {
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn()
    } catch (e) {
      if (i === attempts - 1) throw e
      console.warn(`Operation failed, retrying (${i + 1}/${attempts})...`, e)
    }
  }
  throw new Error('unreachable')
}
/*
 * Parameter detection has four steps:
 * 1. Estimate R (resistance).
 * 2. Estimate Ld (D component of inductance).
 * 3. Estimate Lq (Q component of inductance). After this step, a
 *    START_PARAM_DETECT response with 0 flux linkage is sent.
 * 4. Estimate flux linkage. The motor spins. After this step, another
 *    START_PARAM_DETECT response is sent, which should have nonzero flux
 *    linkage.
 */
export const runParameterDetection = async (
  device: HIDDevice,
  manualParamDetectionParams: IStartParamDetectRequest,
  motorIndex: number = 0,
  polePairCount?: number
): Promise<IStartParamDetectResponse> => {
  // if polePairCount is undefined, we are running parameter detection V1
  // ToDo: clean up dead code and remove polePairCount as parameter to this function.
  const paramDetectTimeoutMs = PARAM_DETECT_V1_TIMEOUT_MS
  if (polePairCount) {
    if (polePairCount === 0 || !isFinite(polePairCount)) {
      throw new Error(
        "Pole pair count is required for parameter detection. Please provide this value on the 'Throttle' tab."
      )
    }
  }

  // TODO: Param Detection must be configured to accept actual individual motor commands for motor controllers with multiple motors.
  // Currently the firmware does not have this fully implemented, so parameter detection assumes a single motor command applies to all motors.

  // Note: _not_ an async call, below!
  // Both paramDetectMaxCurrent and paramDetectMaxRpm are 0 in firmware if not specified.
  const responsePromises = hidRequestMulti(
    device,
    PacketId.START_PARAM_DETECT,
    serializeStartParamDetectRequest({
      paramDetectMaxCurrent: manualParamDetectionParams.paramDetectMaxCurrent,
      paramDetectMaxRpm: manualParamDetectionParams.paramDetectMaxRpm,
      motorIndex: motorIndex
    }),
    [PARAM_DETECT_INITIAL_TIMEOUT_MS, paramDetectTimeoutMs]
  )

  // Resistance and inductance estimation (steps 1-3).
  let response
  try {
    response = await responsePromises[0]
  } catch (e) {
    if (!(e instanceof HIDRequestMultiTimeout)) throw e // Re-throw unexpected errors
    throw new Error(
      `Parameter detection timed out after ${PARAM_DETECT_INITIAL_TIMEOUT_MS / 1000} seconds. ` +
        'Resistance and inductance could not be estimated.'
    )
  }

  if (response.ld === 0 || response.lq === 0 || response.resistance === 0) {
    throw new Error(paramDetectFailMessage(response))
  }

  // Flux linkage estimation (step 4).
  try {
    response = await responsePromises[1]
  } catch (e) {
    if (!(e instanceof HIDRequestMultiTimeout)) throw e // Re-throw unexpected errors
    throw new Error('Flux Linkage estimation timed out.')
  }

  if (
    response.fluxLinkage === 0 ||
    response.ld === 0 ||
    response.lq === 0 ||
    response.resistance === 0
  ) {
    throw new Error(paramDetectFailMessage(response))
  }

  return response
}

const paramDetectFailMessage = (response: IStartParamDetectResponse): string => {
  const disp = (n: number) => n.toExponential(6) // Display number

  if (response.resistance === 0) {
    return 'Resistance could not be estimated.'
  }
  if (response.lq === 0) {
    if (response.ld === 0) {
      return `Inductance was not measured. Is the estimated resistance (${disp(response.resistance)}) correct?`
    } else {
      return `Inductance estimation failed: R = ${disp(response.resistance)}, Ld = ${disp(response.ld)}, Lq = ${disp(response.lq)}`
    }
  }
  return 'Flux linkage estimation failed.' // Unknown failure mode
}

export const runEncoderCalibration = (
  device: HIDDevice,
  motorIndex: number = 0
): Promise<IEncoderCalResultResponse> => {
  return new Promise((resolve, _reject) => {
    let timeout: NodeJS.Timeout | undefined
    const startTimeout = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        device.removeEventListener('inputreport', listener)
        resolve({
          status: SensorCalibStatus.FAILURE_TIMEOUT,
          offset: NaN,
          direction: EncoderDirection.UNKNOWN
        })
      }, ENCODER_CALIBRATION_TIMEOUT_MS)
    }

    const listener = (event: HIDInputReportEvent) => {
      const id = inputReportID(event.data)
      if (id !== PacketId.ENCODER_CAL_RESULT) {
        return
      }

      const response = deserialize(event.data) as IEncoderCalResultResponse

      clearTimeout(timeout)
      device.removeEventListener('inputreport', listener)
      resolve(response)
    }

    startTimeout()
    device.addEventListener('inputreport', listener)
    // TODO: sometimes the device misses this packet, and this should be sent again if no response is received within a short time
    hidSend(device, serializeStartEncoderCalRequest({ motorIndex }))
  })
}

export const runHallSensorCalibration = (
  device: HIDDevice,
  motorIndex: number = 0
): Promise<IHallSensorCalResultResponse> => {
  return new Promise((resolve, _reject) => {
    let timeout: NodeJS.Timeout | undefined
    const startTimeout = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        device.removeEventListener('inputreport', listener)
        resolve({
          status: SensorCalibStatus.FAILURE_TIMEOUT,
          forwardOffset: NaN,
          reverseOffset: NaN,
          direction: HallsensorDirection.UNKNOWN
        })
      }, HALLSENSOR_CALIBRATION_TIMEOUT_MS)
    }

    const listener = (event: HIDInputReportEvent) => {
      const id = inputReportID(event.data)
      if (id !== PacketId.HALL_SENSOR_CAL_RESULT) {
        return
      }

      const response = deserialize(event.data) as IHallSensorCalResultResponse

      clearTimeout(timeout)
      device.removeEventListener('inputreport', listener)
      resolve(response)
    }

    startTimeout()
    device.addEventListener('inputreport', listener)
    hidSend(device, serializeStartHallSensorCalRequest({ motorIndex }))
  })
}

const SENSOR_CAL_STATUS_TIMEOUT_MS = 2000

export const getSensorCalStatus = async (
  device: HIDDevice,
  motorIndex: number = 0
): Promise<IGetSensorCalStatusResponse> => {
  return hidRequest(
    device,
    PacketId.GET_SENSOR_CAL_STATUS,
    serializeGetSensorCalStatusRequest({ motorIndex }),
    SENSOR_CAL_STATUS_TIMEOUT_MS
  )
}

export const runHomingCalibration = (device: HIDDevice): Promise<IHomingCalResultResponse> => {
  return new Promise((resolve, _reject) => {
    let timeout: NodeJS.Timeout | undefined
    const startTimeout = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        device.removeEventListener('inputreport', listener)
        resolve({ status: HomingCalibStatus.FAILURE_TIMEOUT, zeroOffset: NaN })
      }, HOMING_CALIBRATION_TIMEOUT_MS)
    }

    const listener = (event: HIDInputReportEvent) => {
      const id = inputReportID(event.data)
      if (id !== PacketId.HOMING_CAL_RESULT) {
        return
      }

      const response = deserialize(event.data) as IHomingCalResultResponse

      clearTimeout(timeout)
      device.removeEventListener('inputreport', listener)
      resolve(response)
    }

    startTimeout()
    device.addEventListener('inputreport', listener)
    hidSend(device, serializeStartHomingCalRequest({}))
  })
}

export const setPositionLimitToCurrentPos = async (
  device: HIDDevice,
  setMin: boolean,
  setMax: boolean,
  motorIndex: number = 0xff
): Promise<ISetPositionSensorLimitsResponse> => {
  return await hidRequest(
    device,
    PacketId.SET_POSITION_SENSOR_LIMITS,
    serializeSetPositionSensorLimitsRequest({
      setCurrentPosToMin: setMin ? 1 : 0,
      setCurrentPosToMax: setMax ? 1 : 0,
      motorIndex: motorIndex
    })
  )
}

// The latter case is for when generating the error string is expensive
type ErrStringOrCallable = string | (() => string)

const sendFlashMgmtWithResponse = (
  device: HIDDevice,
  cmd: FlashManagementCommand,
  timeoutErr: ErrStringOrCallable,
  request?: Partial<IFlashMgmtRequest>,
  timeoutMs?: number
): Promise<DataView> => {
  return new Promise<DataView>((resolve, reject) => {
    if (timeoutMs === undefined) timeoutMs = FLASH_MGMT_DEFAULT_TIMEOUT_MS
    //avoid lingering listeners by setting up timeout before the listener, so that timeout can clean up the listener if it fires first
    const timeout = setTimeout(() => {
      device.removeEventListener('inputreport', listener)
      const err = typeof timeoutErr === 'string' ? timeoutErr : timeoutErr()
      reject(err)
    }, timeoutMs)
    const listener = (event: HIDInputReportEvent) => {
      if (inputReportID(event.data) !== PacketId.FIRMWARE_UPDATE) return
      if (event.data.getUint8(1) !== cmd) return

      clearTimeout(timeout)
      device.removeEventListener('inputreport', listener)

      if (event.data.byteLength < 12) {
        reject(
          `Device response for command ${cmd} is too short: expected at least 12 bytes, got ${event.data.byteLength}`
        )
        return
      }

      if (request && typeof request.byteOffset === 'number' && OFFSET_BEARING_COMMANDS.has(cmd)) {
        const responseOffset = event.data.getUint32(8, true)
        if (responseOffset !== request.byteOffset) {
          reject(
            `Device response offset (${responseOffset}) does not match expected offset (${request.byteOffset}) for command ${cmd}`
          )
          return
        }
      }
      resolve(event.data)
    }
    device.addEventListener('inputreport', listener)
    hidSend(device, serializeFlashMgmtRequest({ command: cmd, ...request }))
  })
}
// Enqueued here (not inside sendFlashMgmtWithResponse) so chunk requests are serialized
// and retries are handled at the chunk level, avoiding lost or out-of-order packets.
const requestChunk = (
  device: HIDDevice,
  byteOffset: number,
  chunkSize: number
): Promise<DataView> => {
  return enqueueDevice(device, () =>
    sendFlashMgmtWithResponse(
      device,
      FlashManagementCommand.READ_FLASH,
      () => `Firmware read timeout at byte offset ${byteOffset}`,
      { byteOffset, chunkSize }
    )
  )
}

export const readFlashMemory = async (
  device: HIDDevice,
  startOffsetBytes: number,
  readLenBytes: number,
  callback?: ProgressCb
): Promise<Uint8Array> => {
  if (readLenBytes <= 4) {
    throw new PermanentFailure(`Invalid flash memory read size: ${readLenBytes}`)
  }
  if (startOffsetBytes < 0 || startOffsetBytes % 4 !== 0) {
    throw new PermanentFailure(`Invalid flash memory start offset: ${startOffsetBytes}`)
  }

  const readLenWords = Math.ceil(readLenBytes / 4)
  const MAX_CHUNK_LEN_WORDS = 13 // With the overhead removed (3 words), there is room for 52 bytes of data

  const startOffsetInWords = startOffsetBytes / 4
  let offsetInWords = startOffsetInWords
  const limitWords = offsetInWords + readLenWords

  let result = new Uint8Array(new ArrayBuffer(readLenBytes))

  while (offsetInWords < limitWords) {
    const chunkSizeWords = Math.min(MAX_CHUNK_LEN_WORDS, limitWords - offsetInWords)
    const response = await requestChunk(device, offsetInWords * 4, chunkSizeWords * 4)

    const readData = new Uint8Array(response.buffer).subarray(12, 12 + chunkSizeWords * 4)
    result.set(readData, (offsetInWords - startOffsetInWords) * 4)

    offsetInWords += chunkSizeWords
    callback?.((offsetInWords - startOffsetInWords) / readLenWords)
  }
  return result
}

export enum PreCheckProblem {
  // These two CRC variants are mutually exclusive
  NoCrcData,
  CrcMismatch,
  NoCStoreSignature,
  // These two ProductCode variants are mutually exclusive
  UnknownDeviceProductCode,
  CStoreProductCodeMismatch,
  // Device is DFU mode but it is not sending flash sector size or cstore offset
  DFUDeviceMissingSomeFlashInfo
}

export const preCheckFileAndDevice = (
  fileBytes: Uint8Array,
  flashAction: FlashAction,
  flashInfo: FlashInfo | undefined,
  productId: number,
  productName: string
): PreCheckProblem[] => {
  let problems: PreCheckProblem[] = []
  if ([FlashAction.FlashExeAndConfig, FlashAction.FlashExecutable].includes(flashAction)) {
    const crcData = getCrcData(fileBytes)
    if (!crcData || !crcData.imageCrc || !crcData.imageSize) {
      problems.push(PreCheckProblem.NoCrcData)
    } else {
      // instead of having to know exactly what type of device firmware we are checking, we check against both CRC algorithms
      // as they are very fast to compute and the odds of an accidental match on a wrong CRC are astronomically low
      if (
        calcCrcMpeg2(fileBytes, crcData.imageSize) !== crcData.imageCrc &&
        calcCrcStm32Flash(fileBytes, crcData.imageSize) !== crcData.imageCrc
      ) {
        problems.push(PreCheckProblem.CrcMismatch)
      }
    }
    // Check for DFU devices that will lose config due to missing flash info
    if (productNameSupportsDFU(productName) && flashInfo !== undefined) {
      const { sectorSize, cstoreOffset } = flashInfo
      if (sectorSize === undefined || cstoreOffset === undefined) {
        problems.push(PreCheckProblem.DFUDeviceMissingSomeFlashInfo)
      }
    }
  }

  // FlashExecutable has no cstore
  if (flashAction !== FlashAction.FlashExecutable) {
    let cstoreByteOffset: number | undefined
    try {
      cstoreByteOffset = getCStoreRegionOffset(fileBytes, true)
    } catch (e) {
      console.error(e)
      problems.push(PreCheckProblem.NoCStoreSignature)
    }
    // The offset must be 0 for FlashConfig, since the whole file must be a cstore.
    if (
      cstoreByteOffset !== 0 &&
      flashAction === FlashAction.FlashConfig &&
      !problems.includes(PreCheckProblem.NoCStoreSignature)
    ) {
      problems.push(PreCheckProblem.NoCStoreSignature)
    }

    if (cstoreByteOffset !== undefined) {
      const cstoreBytes = fileBytes.subarray(cstoreByteOffset)
      let cstore: CStore | undefined
      try {
        cstore = new CStore(tags as TagsToml, codes as CodesToml, cstoreBytes)
      } catch (e) {
        console.error(e)
        problems.push(PreCheckProblem.NoCStoreSignature)
      }
      if (cstore) {
        const productCode = detectProductCode(productId, productName)
        if (productCode === undefined) {
          problems.push(PreCheckProblem.UnknownDeviceProductCode)
        } else if (cstore.header.productCode !== productCode) {
          problems.push(PreCheckProblem.CStoreProductCodeMismatch)
        }
      }
    }
  }
  return problems
}

// Attempt to determine a cstore productCode given product ID and product name
// from USB. Returns undefined if none can be determined.
//
// See products array in codes.toml for valid return values.
//
// See also USB_PID_BASE_VAR and USB_PRODUCT_NAME_VAR in CMakeLists.txt in each
// product FW repo, as well as usb/tinyusb/usb_descriptors.c in
// Salient-Platform.
const detectProductCode = (usbProductId: number, usbProductName: string): number | undefined => {
  const usbProductIdBase = usbProductId & 0xff00
  const nameParts = usbProductName.toLowerCase().split(' ')

  // Determine a set of possible productCodes from usbProductIdBase. Narrow
  // it down further by matching on nameParts.
  switch (usbProductIdBase) {
    case 0x300: {
      if (nameParts.includes('vortex')) return 0x0004
      if (nameParts.includes('supply')) return 0x0008
      break
    }
    case 0x100: {
      if (nameParts.includes('cyclone')) {
        if (nameParts.includes('hp')) return 0x0009
        return 0x0002
      }
      break
    }
    case 0x200: {
      if (nameParts.includes('pulsar')) return 0x0003
      break
    }
    case 0x400: {
      if (nameParts.includes('actuator')) return 0x0005
      if (nameParts.includes('skywalker')) return 0x0007
      break
    }
  }
  return undefined
}

export const flashFirmware = async (
  device: HIDDevice,
  file: File,
  flashAction: FlashAction,
  callback?: ProgressCb
) => {
  if (!isFlashOp(flashAction)) throw new Error('cannot flashFirmware with a non-flash action')
  let updater
  if (supportsDFU(device)) {
    switch (flashAction) {
      case FlashAction.FlashExecutable: {
        // Get flash info before entering DFU mode (to preserve config region)
        const flashInfo = await getFlashInfo(device)
        const dfuDevice = await enterDFUMode(device)
        updater = new FirmwareUpdateDFU(
          dfuDevice,
          callback,
          flashInfo.cstoreOffset,
          flashInfo.sectorSize
        )
        return await updater.run(file, flashAction)
      }
      case FlashAction.FlashConfig:
        // OK for DFU devices
        updater = new FirmwareUpdateHID(device, callback)
        return await updater.run(file, flashAction)
      case FlashAction.FlashExeAndConfig: {
        const bytes = await fileToBytes(file)
        const cstoreByteOffset = getCStoreRegionOffset(bytes)
        const fwBytes = bytes.subarray(0, cstoreByteOffset)
        const cstoreBytes = bytes.subarray(cstoreByteOffset)

        // First, flash cstore with HID updater
        updater = new FirmwareUpdateHID(device)
        await updater.run(cstoreBytes, FlashAction.FlashConfig)

        // Then, flash firmware with DFU updater
        // Get flash info before entering DFU mode (to preserve config region)
        const flashInfo = await getFlashInfo(device)
        const dfuDevice = await enterDFUMode(device)
        updater = new FirmwareUpdateDFU(
          dfuDevice,
          callback,
          flashInfo.cstoreOffset,
          flashInfo.sectorSize
        )
        return await updater.run(fwBytes, FlashAction.FlashExecutable)
      }
      default:
        throw new Error('Unknown flash target: ' + flashAction)
    }
  } else {
    updater = new FirmwareUpdateHID(device, callback)
    return await updater.run(file, flashAction)
  }
}

export const backupFirmware = async (
  device: HIDDevice,
  callback?: ProgressCb
): Promise<Uint8Array> => {
  if (supportsDFU(device)) {
    throw new Error('This device does not support firmware backup')
  }

  let backup = new FirmwareBackupHID(device, callback)

  return await backup.run()
}

export const writeFlashConfig = async (device: HIDDevice, file: File, callback?: ProgressCb) => {
  let updater = new FirmwareUpdateHID(device, callback)

  return await updater.run(file, FlashAction.FlashConfig)
}

// All sizes and offsets are in bytes
// See also: GetFlashInfoCmd_t
export type FlashInfo = {
  flashSize: number
  dualBankSupport: boolean
  bankSize: number
  cstoreOffset?: number
  sectorSize?: number
}

export const getFlashInfo = async (device: HIDDevice, timeoutMs?: number): Promise<FlashInfo> => {
  if (timeoutMs === undefined) {
    timeoutMs = FLASH_MGMT_DEFAULT_TIMEOUT_MS
  }
  const dv = await sendFlashMgmtWithResponse(
    device,
    FlashManagementCommand.GET_FLASH_INFO,
    'Device flash info request timed out',
    undefined,
    timeoutMs
  )

  const flashSize = dv.getUint32(4, true)
  const dualBankSupport = dv.getUint32(8, true) === 1
  const bankSize = dv.getUint32(12, true)
  let cstoreOffset: number | undefined = dv.getUint32(16, true)
  let sectorSize: number | undefined = dv.getUint32(20, true)
  // Assuming cstore region will never be at offset 0; old FW sends 0
  if (cstoreOffset === 0) cstoreOffset = undefined
  // Old FW sends 0 for sector size; treat as undefined
  if (sectorSize === 0) sectorSize = undefined

  console.log('Flash Size is: ', flashSize)
  console.log('Dual Bank Supported: ', dualBankSupport)
  console.log('Bank Size is: ', bankSize)
  if (cstoreOffset !== undefined) console.log('CStore Offset is: 0x' + cstoreOffset.toString(16))
  if (sectorSize !== undefined) console.log('Sector Size is: 0x' + sectorSize.toString(16))

  return { flashSize, dualBankSupport, bankSize, cstoreOffset, sectorSize }
}

// Note: single bank (DFU) devices use this for flashing config, despite the "HID" in the name.
class FirmwareUpdateHID {
  private device: HIDDevice
  private callback?: ProgressCb

  constructor(device: HIDDevice, callback?: ProgressCb) {
    this.device = device
    this.callback = callback
  }

  run = async (fileOrBytes: File | Uint8Array, target: FlashAction): Promise<boolean> => {
    let attempts = 3
    while (attempts-- > 0) {
      try {
        switch (target) {
          case FlashAction.FlashExecutable:
            await this.eraseBank()
            await this.flashBinary(fileOrBytes, target)
            await this.finalizeFirmwareUpdate()
            break
          case FlashAction.FlashConfig:
            await this.eraseConfigRegion()
            await this.flashBinary(fileOrBytes, target)
            await this.reloadFirmwareConfig()
            break
          case FlashAction.FlashExeAndConfig:
            await this.eraseBank()
            await this.eraseConfigRegion()
            await this.flashBinary(fileOrBytes, target)
            // No need for reloadFirmwareConfig here because MCU will be reset
            await this.finalizeFirmwareUpdate()
            break
          default:
            console.error('Unknown flash target', target)
            return false
        }

        return true
      } catch (e) {
        console.warn(e)
        if (attempts > 0) {
          if (e instanceof PermanentFailure) {
            break
          } else {
            console.warn(`Retrying (${attempts} more attempts)`)
          }
        }
      }
    }

    return false
  }

  private eraseBank = async (): Promise<DataView> =>
    sendFlashMgmtWithResponse(
      this.device,
      FlashManagementCommand.ERASE_BANK,
      'Firmware update timeout while attempting to erase bank',
      {},
      ERASE_BANK_TIMEOUT_MS
    )

  private eraseConfigRegion = async (): Promise<DataView> =>
    sendFlashMgmtWithResponse(
      this.device,
      FlashManagementCommand.ERASE_FLASH_CONFIG,
      'Flash config timed out while attempting to erase the config region'
    )

  private bytesToWords = (bytes: Uint8Array): Uint32Array => {
    const buf = new ArrayBuffer(Math.ceil(bytes.byteLength / 4) * 4)
    const u8 = new Uint8Array(buf)
    u8.set(bytes, 0)
    return new Uint32Array(u8.buffer)
  }
  private sendChunk = (
    cmd: FlashManagementCommand,
    chunk: Uint32Array,
    byteOffset: number
  ): Promise<DataView> => {
    return enqueueDevice(this.device, () =>
      sendFlashMgmtWithResponse(
        this.device,
        cmd,
        () => `Firmware update timeout at byte offset ${byteOffset}`,
        { chunk, byteOffset }
      )
    )
  }

  private compareChunks = (chunk1: Uint32Array, chunk2: Uint32Array): boolean => {
    if (chunk1.byteLength !== chunk2.byteLength) {
      return false
    }

    for (const [i, word] of chunk1.entries()) {
      if (word !== chunk2[i]) {
        return false
      }
    }

    return true
  }

  private flashBinary = async (
    fileOrBytes: File | Uint8Array,
    action: FlashAction
  ): Promise<void> => {
    if (!isFlashOp(action)) throw new Error('cannot flashBinary with a non-flash action')
    const bytes = fileOrBytes instanceof Uint8Array ? fileOrBytes : await fileToBytes(fileOrBytes)
    let cstoreWordOffset: number | undefined
    if (action === FlashAction.FlashExeAndConfig) {
      // First part of the file is program memory; second, and smaller, part is cstore bin
      cstoreWordOffset = getCStoreRegionOffset(bytes) / 4
    }
    const words = this.bytesToWords(bytes)

    let offset = 0
    while (offset < words.length) {
      let currentChunkSize = Math.min(PROGRAM_CHUNK_WORD_SIZE, words.length - offset)
      if (cstoreWordOffset !== undefined && offset < cstoreWordOffset) {
        currentChunkSize = Math.min(PROGRAM_CHUNK_WORD_SIZE, cstoreWordOffset - offset)
      }
      const currentChunk = words.slice(offset, offset + currentChunkSize)

      let response
      switch (action) {
        case FlashAction.FlashExecutable:
          response = await this.sendChunk(
            FlashManagementCommand.PROGRAM_MEMORY,
            currentChunk,
            offset * 4
          )
          break
        case FlashAction.FlashConfig:
          response = await this.sendChunk(
            FlashManagementCommand.WRITE_FLASH_CONFIG,
            currentChunk,
            offset * 4
          )
          break
        case FlashAction.FlashExeAndConfig:
          if (offset >= cstoreWordOffset!) {
            response = await this.sendChunk(
              FlashManagementCommand.WRITE_FLASH_CONFIG,
              currentChunk,
              (offset - cstoreWordOffset!) * 4
            )
          } else {
            response = await this.sendChunk(
              FlashManagementCommand.PROGRAM_MEMORY,
              currentChunk,
              offset * 4
            )
          }
          break
        default:
          return
      }

      if (
        !this.compareChunks(
          currentChunk,
          new Uint32Array(response.buffer.slice(12, 12 + currentChunkSize * 4))
        )
      ) {
        throw new Error(`Corrupted firmware update at byte offset: ${offset * 4}`)
      }

      offset += currentChunkSize
      this.callback?.(offset / words.length)
    }
  }

  private finalizeFirmwareUpdate = async () => {
    await hidSend(
      this.device,
      serializeFlashMgmtRequest({ command: FlashManagementCommand.END_OF_UPDATE })
    )
  }

  private reloadFirmwareConfig = async () => {
    await hidSend(
      this.device,
      serializeFlashMgmtRequest({ command: FlashManagementCommand.RELOAD_CONFIG })
    )
  }
}

class FirmwareBackupHID {
  private device: HIDDevice
  private callback?: ProgressCb

  constructor(device: HIDDevice, callback?: ProgressCb) {
    this.device = device
    this.callback = callback
  }

  run = async (): Promise<Uint8Array> => {
    let attempts = 3
    let lastError: unknown
    while (attempts-- > 0) {
      try {
        const info = await getFlashInfo(this.device)
        return await readFlashMemory(this.device, 0, info.bankSize, this.callback)
      } catch (e) {
        lastError = e
        console.warn(e)
        if (attempts > 0) {
          if (e instanceof PermanentFailure) {
            throw e
          } else {
            console.warn(`Retrying ({attempts} more attempts)`)
          }
        }
      }
    }

    throw lastError
  }
}

/**
 * Zero the DARE sensor encoders and persist the zero offsets to CStore.
 *
 * IMPORTANT NOTE: this flashes cstore! This is contrary to the UX for the rest of S-Lab.
 *
 * Architecture mirrors CStoreEditor's exportConfig():
 * 1. Sends ZERO_DARE_SENSOR and waits for the firmware response (with timeout).
 *    - New firmware: returns the raw 14-bit angles for both encoders.
 *    - Old firmware: no response → The empty object is returned.
 * 2. Creates modify ops + UpdateHeader (build date), modifies CStore, exports, FLASHES.
 * 3. Returns the CStore binary and both encoder zero positions for verification.
 */
export const zeroDareSensorAndSave = async (
  device: HIDDevice,
  cstore: CStore
): Promise<{ cstoreBin?: Uint8Array; encoder0ZeroPos?: number; encoder1ZeroPos?: number }> => {
  // 1. Send ZERO_DARE_SENSOR and try to get response with angles.
  //    Old firmware returns COMMAND_SUCCESS_NO_RESPONSE (no packet sent back),
  //    so hidRequest will time out — that's OK, we handle it gracefully.
  let zeroResponse: IZeroDareSensorResponse | undefined
  try {
    zeroResponse = await hidRequest(
      device,
      PacketId.ZERO_DARE_SENSOR,
      serializeZeroDareSensorRequest({}),
      ZERO_DARE_SENSOR_TIMEOUT_MS
    )
  } catch (e) {
    console.warn('ZERO_DARE_SENSOR response not received (old firmware?):', e)
    return {}
  }

  // 2. Build modify ops, export to a cstore bin, and flash the device.
  const d = new Date()
  const updateHeaderOp: Op = {
    type: OpType.UpdateHeader,
    updates: { buildYYYYMMDD: [d.getFullYear(), d.getMonth() + 1, d.getDate()] }
  }

  const modifyOps: Op[] = [
    { type: OpType.UpsertTag, id: 'DS_ZeroPositionEncoder0', value: zeroResponse.encoder0ZeroPos },
    { type: OpType.UpsertTag, id: 'DS_ZeroPositionEncoder1', value: zeroResponse.encoder1ZeroPos },
    updateHeaderOp
  ]

  const newCstore = cstore.modify(modifyOps)
  const cstoreBin = newCstore.export()

  const updater = new FirmwareUpdateHID(device)
  await updater.run(cstoreBin, FlashAction.FlashConfig)
  console.log('Flashed cstore with modified zero positions')

  return {
    cstoreBin,
    encoder0ZeroPos: zeroResponse.encoder0ZeroPos,
    encoder1ZeroPos: zeroResponse.encoder1ZeroPos
  }
}

/**
 * Zero the DARE sensor encoders and optionally enter calibration mode.
 *
 * Unlike zeroDareSensorAndSave, this does NOT read/flash CStore — it only
 * sends the ZERO_DARE_SENSOR command and returns the encoder zero offsets.
 * When enterCalibMode is true, SubCommand[0] is set to 1, which tells the
 * firmware to switch to accumulator-based unlimited-range position tracking.
 *
 * The command is sent fire-and-forget: the firmware's USB transmit queue
 * (depth 4) can overflow with telemetry packets before the response is
 * dequeued, so the response is not reliably delivered.  We attempt a short
 * read — if it arrives, great; otherwise we proceed without it.
 */
export const zeroDareSensor = async (
  device: HIDDevice,
  enterCalibMode: boolean = false
): Promise<IZeroDareSensorResponse | undefined> => {
  // Build packet with SubCommand[0] = calibMode flag
  const dv = new DataView(new ArrayBuffer(64))
  dv.setUint8(0, PacketId.ZERO_DARE_SENSOR)
  dv.setUint8(1, enterCalibMode ? 1 : 0)

  try {
    return await hidRequest(
      device,
      PacketId.ZERO_DARE_SENSOR,
      dv,
      500 // Short timeout — firmware response is unreliable
    )
  } catch {
    // Response not received — command was still sent and sensor will zero.
    return undefined
  }
}

/**
 * Read back the cached DARE sensor zero offsets without triggering a new
 * zero sequence.  Sends ZERO_DARE_SENSOR with SubCommand[0] = 2 — a
 * lightweight synchronous read that does not involve DMA or sleep, so the
 * USB response arrives reliably (unlike the fire-and-forget zero command).
 */
export const readDareZeroResult = async (
  device: HIDDevice
): Promise<IZeroDareSensorResponse | undefined> => {
  const dv = new DataView(new ArrayBuffer(64))
  dv.setUint8(0, PacketId.ZERO_DARE_SENSOR)
  dv.setUint8(1, 2) // SubCommand[0] = 2 → read-back only

  try {
    return await hidRequest(device, PacketId.ZERO_DARE_SENSOR, dv, 1000)
  } catch {
    return undefined
  }
}
